import React, { useCallback, useEffect, useState } from 'react';
import { GripVertical, Plus, Trash2 } from 'lucide-react';

import { ToggleSwitch } from '../common';
import AddRoutingRuleModal, { type AddRoutingRuleSubmitMeta } from '../modals/AddRoutingRuleModal';
import { authService } from '../../auth/authService';
import { isSpokeFacilityUser } from '../../utils/permissions';
import { ICON_STROKE_WIDTH } from '../../constants';
import { formatModalityDisplayLabel } from '../../utils/modalityUtils';
import { formatPriorityDisplay } from '../worklist/imagingWorklistTableUtils';
import {
  createDicomRoutingRule,
  deleteDicomRoutingRule,
  getDicomRoutingRules,
  reorderDicomRoutingRules,
  updateDicomRoutingRule,
  type CreateDicomRoutingRuleRequest,
  type DicomRoutingRule,
} from '../../services/dicomRoutingRuleService';
import {
  listStudyAssignmentRules,
  type StudyAssignmentRule,
} from '../../services/studyAssignmentRuleService';
import { useReorderableRows } from '../../hooks';
import { moveItem } from '../../utils/moveItem';
import {
  buildModalityLabelOptions,
  findRoutingRuleModalityConflict,
  hasPrefetchedCrossRuleValidationData,
} from '../../utils/risRuleModalityConflictUtils';
import '../../pages/admin/Configuration.css';

interface RoutingRule {
  id: string;
  name: string;
  matchCriteria: string[];
  destination: string;
  destinationDetail: string;
  active: boolean;
  precedence: number;
}

function buildReorderPayload(
  nextRules: RoutingRule[],
  previousRules: RoutingRule[]
): { id: number; precedence: number }[] {
  const sortedPrecedences = [...previousRules]
    .map(rule => rule.precedence)
    .sort((left, right) => left - right);

  return nextRules.map((rule, index) => ({
    id: Number(rule.id),
    precedence: sortedPrecedences[index] ?? (index + 1) * 10,
  }));
}

function mapApiRuleToTableRow(
  rule: DicomRoutingRule,
  meta?: AddRoutingRuleSubmitMeta
): RoutingRule {
  const hubName = meta?.hubFacilityName ?? rule.HubFacility?.facility_name;
  const studyTypeLabels = meta?.studyTypeLabels ?? [];
  const studyTypeCount = rule.radiology_test_ids?.length ?? 0;
  const matchCriteria = [
    ...(rule.modalities?.map(modality => formatModalityDisplayLabel(modality.name)) ?? []),
    ...(rule.match_ae_title?.trim() ? [rule.match_ae_title.trim()] : []),
    ...(hubName ? [hubName] : []),
    ...(rule.match_priority?.trim()
      ? [formatPriorityDisplay(rule.match_priority) || rule.match_priority]
      : []),
    ...studyTypeLabels,
    ...(studyTypeLabels.length === 0 && studyTypeCount > 0
      ? [`${studyTypeCount} study type${studyTypeCount === 1 ? '' : 's'}`]
      : []),
  ];

  return {
    id: String(rule.id),
    name: rule.name,
    matchCriteria,
    destination: rule.destination_type,
    destinationDetail: rule.destination_ae,
    active: rule.is_active,
    precedence: rule.precedence,
  };
}

function mapApiRulesToTableRows(rules: DicomRoutingRule[]): RoutingRule[] {
  return [...rules]
    .sort((left, right) => left.precedence - right.precedence)
    .map(rule => mapApiRuleToTableRow(rule));
}

export default function RoutingRulesSection() {
  const dashboardUser = authService.getUser();
  const isSpokeFacility = isSpokeFacilityUser(dashboardUser);
  const [routingRules, setRoutingRules] = useState<RoutingRule[]>([]);
  const [routingRulesApiData, setRoutingRulesApiData] = useState<DicomRoutingRule[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLoadingRules, setIsLoadingRules] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [togglingRuleId, setTogglingRuleId] = useState<string | null>(null);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [activeStudyAssignmentRules, setActiveStudyAssignmentRules] = useState<
    StudyAssignmentRule[] | null
  >(null);

  const loadRoutingRules = useCallback(async () => {
    setIsLoadingRules(true);
    setLoadError(null);

    const facilityId = Number(authService.getFacilityId());
    const canPrefetchAssignmentRules = Number.isInteger(facilityId) && facilityId > 0;

    const [routingResult, assignmentResult] = await Promise.all([
      getDicomRoutingRules(),
      canPrefetchAssignmentRules
        ? listStudyAssignmentRules(facilityId)
        : Promise.resolve({ success: false as const }),
    ]);

    if (routingResult.success && routingResult.data) {
      setRoutingRulesApiData(routingResult.data);
      setRoutingRules(mapApiRulesToTableRows(routingResult.data));
    } else {
      setRoutingRulesApiData([]);
      setRoutingRules([]);
      setLoadError(routingResult.error || 'Failed to load routing rules');
    }

    if (assignmentResult.success && Array.isArray(assignmentResult.data)) {
      setActiveStudyAssignmentRules(assignmentResult.data.filter(rule => rule.isActive));
    } else {
      setActiveStudyAssignmentRules(null);
    }

    setIsLoadingRules(false);
  }, []);

  useEffect(() => {
    if (!isSpokeFacility) {
      setIsLoadingRules(false);
      return;
    }
    loadRoutingRules();
  }, [isSpokeFacility, loadRoutingRules]);

  const handleToggleActive = async (id: string, active: boolean) => {
    const previousActive = routingRules.find(rule => rule.id === id)?.active ?? !active;
    const apiRule = routingRulesApiData.find(rule => String(rule.id) === id);

    if (active && apiRule && hasPrefetchedCrossRuleValidationData(activeStudyAssignmentRules)) {
      const modalityConflict = findRoutingRuleModalityConflict(
        apiRule.modality_ids ?? [],
        activeStudyAssignmentRules,
        buildModalityLabelOptions(apiRule, activeStudyAssignmentRules),
        true,
        'activate'
      );
      if (modalityConflict) {
        setLoadError(modalityConflict);
        return;
      }
    }

    setTogglingRuleId(id);
    setLoadError(null);
    setRoutingRules(prev => prev.map(rule => (rule.id === id ? { ...rule, active } : rule)));

    const result = await updateDicomRoutingRule(id, { is_active: active });
    if (!result.success || !result.data) {
      setRoutingRules(prev =>
        prev.map(rule => (rule.id === id ? { ...rule, active: previousActive } : rule))
      );
      setLoadError(result.error || 'Failed to update routing rule');
      setTogglingRuleId(null);
      return;
    }

    setRoutingRules(prev =>
      prev.map(rule => (rule.id === id ? { ...rule, active: result.data.is_active } : rule))
    );
    setTogglingRuleId(null);
  };

  const persistReorder = useCallback(
    async (nextRules: RoutingRule[], previousRules: RoutingRule[]) => {
      setReordering(true);
      setRoutingRules(nextRules);

      const result = await reorderDicomRoutingRules(buildReorderPayload(nextRules, previousRules));
      setReordering(false);

      if (result.success && Array.isArray(result.data)) {
        setRoutingRules(mapApiRulesToTableRows(result.data));
        return;
      }

      setRoutingRules(previousRules);
      setLoadError(result.error || 'Failed to reorder routing rules');
    },
    []
  );

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      const previous = routingRules;
      const next = moveItem(routingRules, fromIndex, toIndex);
      void persistReorder(next, previous);
    },
    [routingRules, persistReorder]
  );

  const { dragIndex, getRowProps, getHandleProps } = useReorderableRows({
    disabled: reordering,
    onReorder: handleReorder,
  });

  const handleDelete = async (id: string) => {
    setDeletingRuleId(id);
    setLoadError(null);

    const result = await deleteDicomRoutingRule(id);
    if (!result.success) {
      setLoadError(result.error || 'Failed to delete routing rule');
      setDeletingRuleId(null);
      return;
    }

    setRoutingRules(prev => prev.filter(rule => rule.id !== id));
    setDeletingRuleId(null);
  };

  const handleAddRule = () => {
    setIsAddModalOpen(true);
  };

  const handleAddRuleSubmit = async (
    payload: CreateDicomRoutingRuleRequest,
    meta: AddRoutingRuleSubmitMeta
  ) => {
    const result = await createDicomRoutingRule(payload);
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create routing rule');
    }

    await loadRoutingRules();
  };

  if (!isSpokeFacility) {
    return null;
  }

  return (
    <>
      <header className="ris-configuration-header ris-configuration-header--actions-only">
        <button
          type="button"
          className="ris-configuration-add-btn"
          onClick={handleAddRule}
        >
          <Plus
            strokeWidth={ICON_STROKE_WIDTH}
            size={16}
          />
          Add routing rule
        </button>
      </header>

      <section className="ris-configuration-card">
        <div className="ris-configuration-card-header">
          <h2 className="ris-configuration-card-title">DICOM routing rules</h2>
          <p className="ris-configuration-card-description">
            Defines how the DICOM listener (PACS/VNA integration) should route incoming studies.
            Rules are evaluated by the ingest service on the server side, not at front-desk order
            creation. Most-specific match wins; inactive rules are ignored. Drag rows to set
            evaluation order.
          </p>
        </div>

        {loadError ? (
          <p
            className="ris-configuration-load-error"
            role="alert"
          >
            {loadError}
          </p>
        ) : null}

        <div className="ris-configuration-table-wrap">
          <table className="ris-configuration-table">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="ris-configuration-drag-col"
                >
                  <span className="sr-only">Order</span>
                </th>
                <th scope="col">#</th>
                <th scope="col">Name</th>
                <th scope="col">Match criteria</th>
                <th scope="col">Destination</th>
                <th scope="col">Active</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoadingRules ? (
                <tr>
                  <td
                    colSpan={7}
                    className="ris-configuration-empty"
                  >
                    Loading routing rules…
                  </td>
                </tr>
              ) : routingRules.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="ris-configuration-empty"
                  >
                    No routing rules configured.
                  </td>
                </tr>
              ) : (
                routingRules.map((rule, index) => (
                  <tr
                    key={rule.id}
                    {...getRowProps(index)}
                    className={
                      [
                        dragIndex === index ? 'ris-configuration-row--dragging' : '',
                        !rule.active ? 'ris-configuration-row--inactive' : '',
                      ]
                        .filter(Boolean)
                        .join(' ') || undefined
                    }
                  >
                    <td className="ris-configuration-drag-col">
                      <span
                        className="ris-configuration-drag-handle"
                        aria-hidden="true"
                        {...getHandleProps(index)}
                      >
                        <GripVertical
                          strokeWidth={ICON_STROKE_WIDTH}
                          size={16}
                        />
                      </span>
                    </td>
                    <td className="ris-configuration-cell-index">{index + 1}</td>
                    <td className="ris-configuration-cell-name">{rule.name}</td>
                    <td>
                      <div className="ris-configuration-tags">
                        {rule.matchCriteria.length > 0 ? (
                          rule.matchCriteria.map(tag => (
                            <span
                              key={`${rule.id}-${tag}`}
                              className="ris-configuration-tag"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="ris-configuration-tag ris-configuration-tag--empty">
                            No criteria
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="ris-configuration-destination">
                        <span className="ris-configuration-destination-name">
                          {rule.destination}
                        </span>
                        {rule.destinationDetail ? (
                          <span className="ris-configuration-destination-detail">
                            {rule.destinationDetail}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <ToggleSwitch
                        checked={rule.active}
                        disabled={togglingRuleId === rule.id || reordering}
                        onChange={checked => handleToggleActive(rule.id, checked)}
                      />
                    </td>
                    <td>
                      <div className="ris-configuration-actions">
                        <button
                          type="button"
                          className="ris-configuration-action-btn ris-configuration-action-btn--delete"
                          onClick={() => handleDelete(rule.id)}
                          disabled={deletingRuleId === rule.id || reordering}
                          aria-label={`Delete ${rule.name}`}
                        >
                          <Trash2
                            strokeWidth={ICON_STROKE_WIDTH}
                            size={18}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <AddRoutingRuleModal
        isOpen={isAddModalOpen}
        activeStudyAssignmentRules={activeStudyAssignmentRules}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddRuleSubmit}
      />
    </>
  );
}
